/* Xiaomi · 15-step Partner Journey timeline (LIVE 2026-06-02)
 *
 * Reusable component used in THREE surfaces:
 *   1. Inside the Gift Ops AdvanceDrawer (side panel)
 *   2. The standalone page /xiaomi/partners/:retailerId/journey/...
 *   3. Inline on the partner 360 view
 *
 * Doctrine: any Xiaomi-access user can mark any step done (or undo it).
 * Every action is audit-logged server-side.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, Circle, Loader2, Undo2, MessageSquare } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const PHASE_COLOR = {
  intent:    { bg: '#E0E7FF', tone: '#3730A3' },
  plan:      { bg: '#E0E7FF', tone: '#3730A3' },
  dispatch:  { bg: '#FEF3C7', tone: '#92400E' },
  send:      { bg: '#FEF3C7', tone: '#92400E' },
  booking:   { bg: '#DBEAFE', tone: '#1E3A8A' },
  delivery:  { bg: '#FAE8FF', tone: '#581C87' },
  fulfil:    { bg: '#FAE8FF', tone: '#581C87' },
  chase:     { bg: '#FED7AA', tone: '#7C2D12' },
  feedback:  { bg: '#CCFBF1', tone: '#0F766E' },
  report:    { bg: '#DCFCE7', tone: '#166534' },
};

const OWNER_COLOR = {
  Kavita: '#FF6900',
  Ayush:  '#9A4B0E',
  Rajan:  '#0F766E',
  ZSM:    '#5B21B6',
  Mira:   '#1B2A4A',
  System: '#6B7280',
};

export default function JourneyTimeline({
  retailerId, benefitId, monthYm, compact = false, onChange,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyStep, setBusyStep] = useState(null);
  const [stepNote, setStepNote] = useState({});

  const load = useCallback(async () => {
    if (!retailerId || !benefitId || !monthYm) return;
    setLoading(true);
    try {
      const r = await api.get(
        `/xiaomi/journey/partner/${retailerId}/${benefitId}/${monthYm}`,
      );
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load journey');
    } finally { setLoading(false); }
  }, [retailerId, benefitId, monthYm]);

  useEffect(() => { load(); }, [load]);

  const advance = async (n) => {
    setBusyStep(n);
    try {
      await api.post(
        `/xiaomi/journey/partner/${retailerId}/${benefitId}/${monthYm}/advance`,
        { step: n, note: (stepNote[n] || '').trim() || null },
      );
      setStepNote((s) => ({ ...s, [n]: '' }));
      toast.success(`Step ${n} marked done`);
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to advance');
    } finally { setBusyStep(null); }
  };

  const undo = async (n) => {
    if (!window.confirm(`Undo step ${n}?`)) return;
    setBusyStep(n);
    try {
      await api.post(
        `/xiaomi/journey/partner/${retailerId}/${benefitId}/${monthYm}/undo`,
        { step: n },
      );
      toast.success(`Step ${n} undone`);
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to undo');
    } finally { setBusyStep(null); }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-soft text-sm py-4"
        data-testid="journey-loading">
        <Loader2 size={14} className="animate-spin" /> Loading journey…
      </div>
    );
  }
  if (!data) return null;
  const { steps, pct_complete, current_step, template_label, template_color } = data;

  return (
    <div data-testid={`journey-timeline-${retailerId}`}>
      {/* Progress header */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] uppercase tracking-wider"
            style={{ color: template_color || '#6B7280', fontWeight: 700 }}>
            {template_label || 'Journey'}
          </div>
          <div className="text-[11px] tabular-nums" style={{ color: '#6B7280' }}>
            <strong style={{ color: '#1B2A4A' }}>{pct_complete}%</strong> complete · step{' '}
            <strong style={{ color: '#1B2A4A' }}>
              {current_step > steps.length ? '✓ done' : current_step}
            </strong>
            {' '}of {steps.length}
          </div>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#F0EEEA' }}>
          <div className="h-full rounded-full transition-all"
            style={{
              width: `${pct_complete}%`,
              background: pct_complete === 100 ? '#0F766E' : (template_color || '#FF6900'),
            }} />
        </div>
      </div>

      {/* Step list */}
      <ol className="space-y-1.5">
        {steps.map((s) => {
          const phase = PHASE_COLOR[s.phase] || { bg: '#F3F4F6', tone: '#374151' };
          const ownerColor = OWNER_COLOR[s.owner] || '#6B7280';
          const isCurrent = s.n === current_step && !s.done;
          const isBusy = busyStep === s.n;
          return (
            <li key={s.n}
              data-testid={`journey-step-${s.n}`}
              className="rounded-md border flex items-start gap-2 p-2"
              style={{
                borderColor: s.done ? '#86EFAC' : (isCurrent ? template_color || '#FF6900' : '#E5E1DC'),
                background: s.done ? '#F0FDF4' : (isCurrent ? '#FFF7ED' : 'white'),
              }}>
              <button
                onClick={() => (s.done ? undo(s.n) : advance(s.n))}
                disabled={isBusy}
                title={s.done ? `Done by ${s.done_by} — click to undo` : 'Mark this step done'}
                className="flex-shrink-0 mt-0.5 disabled:opacity-50"
                data-testid={`journey-step-toggle-${s.n}`}>
                {isBusy ? (
                  <Loader2 size={16} className="animate-spin" style={{ color: '#6B7280' }} />
                ) : s.done ? (
                  <CheckCircle2 size={16} style={{ color: '#0F766E' }} />
                ) : (
                  <Circle size={16} style={{ color: '#9CA3AF' }} />
                )}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] font-mono tabular-nums"
                    style={{ color: '#9CA3AF' }}>#{String(s.n).padStart(2, '0')}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: phase.bg, color: phase.tone }}>
                    {s.phase}
                  </span>
                  <span className="text-[10px] font-semibold"
                    style={{ color: ownerColor }}>{s.owner}</span>
                  {s.done && (
                    <span className="text-[10px]" style={{ color: '#0F766E' }}>
                      ✓ {s.done_by} · {s.done_at?.slice(0, 16).replace('T', ' ')}
                    </span>
                  )}
                </div>
                <div className={`text-[12px] mt-0.5 ${s.done ? 'line-through' : ''}`}
                  style={{ color: s.done ? '#6B7280' : '#15110d' }}>
                  {s.label}
                </div>
                {s.note && (
                  <div className="text-[11px] italic mt-1 px-2 py-1 rounded"
                    style={{ background: '#FEF9C3', color: '#713F12' }}>
                    {s.note}
                  </div>
                )}
                {!s.done && !compact && isCurrent && (
                  <div className="flex items-center gap-1 mt-1.5">
                    <input
                      type="text"
                      value={stepNote[s.n] || ''}
                      onChange={(e) => setStepNote((p) => ({ ...p, [s.n]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') advance(s.n); }}
                      placeholder="optional note (e.g. PO# / AWB / venue name)"
                      className="flex-1 text-[11px] px-2 py-1 rounded border bg-white"
                      style={{ borderColor: '#E5E1DC' }}
                      data-testid={`journey-step-note-${s.n}`} />
                    <button onClick={() => advance(s.n)} disabled={isBusy}
                      className="text-[11px] px-2 py-1 rounded text-white font-semibold disabled:opacity-50"
                      style={{ background: template_color || '#FF6900' }}
                      data-testid={`journey-step-advance-${s.n}`}>
                      Mark done
                    </button>
                  </div>
                )}
                {s.done && !compact && (
                  <button onClick={() => undo(s.n)} disabled={isBusy}
                    className="text-[10px] underline mt-0.5 inline-flex items-center gap-1"
                    style={{ color: '#6B7280' }}
                    data-testid={`journey-step-undo-${s.n}`}>
                    <Undo2 size={10} /> undo
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {!compact && (
        <JourneyNotesLog
          notes={data.notes || []}
          onAdd={async (text) => {
            try {
              await api.post(
                `/xiaomi/journey/partner/${retailerId}/${benefitId}/${monthYm}/note`,
                { text },
              );
              await load();
              return true;
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Note save failed');
              return false;
            }
          }} />
      )}
    </div>
  );
}


function JourneyNotesLog({ notes, onAdd }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="pt-3 mt-3 border-t" style={{ borderColor: '#E5E1DC' }}
      data-testid="journey-notes-log">
      <div className="text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1"
        style={{ color: '#6B7280' }}>
        <MessageSquare size={11} /> Free-text notes ({notes.length})
      </div>
      {notes.length === 0 ? (
        <div className="text-[11px] italic text-soft mb-2">
          No journey-wide notes yet. Use step-level notes above for context that fits a specific step.
        </div>
      ) : (
        <div className="space-y-1.5 mb-2 max-h-40 overflow-y-auto">
          {notes.map((n, i) => (
            <div key={i} className="rounded p-1.5 text-[11px]"
              style={{ background: '#FFF9EB', border: '1px solid #FDE68A' }}>
              <div className="text-[10px] mb-0.5" style={{ color: '#6B7280' }}>
                {n.by || '?'}
                {n.ts ? ` · ${new Date(n.ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}` : ''}
              </div>
              <div className="text-[#15110d] whitespace-pre-wrap">{n.text}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <input type="text" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key === 'Enter' && text.trim() && !busy) {
              setBusy(true);
              const ok = await onAdd(text.trim());
              if (ok) setText('');
              setBusy(false);
            }
          }}
          placeholder="Add journey-wide note (vendor delay, dealer concern…)"
          className="flex-1 text-[11px] px-2 py-1 rounded border bg-white"
          style={{ borderColor: '#E5E1DC' }}
          data-testid="journey-notes-input" />
        <button onClick={async () => {
          if (!text.trim() || busy) return;
          setBusy(true);
          const ok = await onAdd(text.trim());
          if (ok) setText('');
          setBusy(false);
        }}
          disabled={busy || !text.trim()}
          className="text-[11px] px-2 py-1 rounded text-white font-semibold disabled:opacity-40"
          style={{ background: '#1B2A4A' }}
          data-testid="journey-notes-add">
          + Note
        </button>
      </div>
    </div>
  );
}
